import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';

function CustomerResultCard({ result, onClick }) {
  const { customer, csat, cost, responseTime, timing, wasOptimal } = result;
  
  // Tier color mapping
  const tierColors = {
    VIP: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
    Business: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    Standard: 'bg-gray-500/20 text-gray-400 border-gray-500/50'
  };
  
  const tierColor = tierColors[customer.profile.tier] || tierColors.Standard;

  // CSAT emotion
  const emotion = csat >= 85 ? '😊' : csat >= 65 ? '🙂' : csat >= 40 ? '😐' : '😠';
  const csatColor =
    csat >= 85 ? 'emerald' : csat >= 65 ? 'blue' : csat >= 40 ? 'amber' : 'red';

  // Timing indicator
  const timingIcon = timing === 'proactive' ? '⚡' : timing === 'reactive' ? '✓' : '❌';
  const timingColor = timing === 'proactive' ? 'emerald' : timing === 'reactive' ? 'blue' : 'red';

  // Performance label
  const performance = wasOptimal ? 'OPTIMAL' : csat >= 80 ? 'GOOD' : csat >= 60 ? 'OK' : 'POOR';
  const perfColor = wasOptimal ? 'emerald' : csat >= 80 ? 'blue' : csat >= 60 ? 'amber' : 'red';

  const formatTime = (seconds) => {
    return `${(seconds / 60).toFixed(1)}m`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.0 }}
      onClick={onClick}
      className="bg-slate-900 border border-white/10 rounded-xl p-4 hover:bg-slate-800 hover:border-white/20 cursor-pointer transition-all group"
    >
      {/* Customer info */}
      <div className="flex items-center gap-3 mb-3">
        <img
          src={customer.profile.avatar}
          alt={customer.profile.name}
          className="w-12 h-12 rounded-full ring-2 ring-white/10"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-gray-50 truncate">{customer.profile.name}</h4>
            <Badge className={`text-[10px] px-1.5 py-0 border ${tierColor}`}>
              {customer.profile.tier}
            </Badge>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {customer.trip.from} → {customer.trip.to}
          </p>
        </div>
      </div>

      {/* CSAT */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{emotion}</span>
        <span className={`text-xl font-bold ${
          csatColor === 'emerald' ? 'text-emerald-400' :
          csatColor === 'blue' ? 'text-blue-400' :
          csatColor === 'amber' ? 'text-amber-400' : 'text-red-400'
        }`}>{csat}</span>
      </div>

      {/* Cost */}
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-gray-500">Cost</span>
        <span className="font-mono text-gray-50">€{cost}</span>
      </div>

      {/* Response time */}
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-gray-500">Time</span>
        <span className={`font-mono flex items-center gap-1 ${
          timingColor === 'emerald' ? 'text-emerald-400' :
          timingColor === 'blue' ? 'text-blue-400' : 'text-red-400'
        }`}>
          {formatTime(responseTime)}
          <span className="text-base">{timingIcon}</span>
        </span>
      </div>

      {/* Performance badge */}
      <div
        className={`text-xs font-semibold text-center px-2 py-1 rounded border ${
          perfColor === 'emerald' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' :
          perfColor === 'blue' ? 'bg-blue-500/20 text-blue-400 border-blue-500/50' :
          perfColor === 'amber' ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' :
          'bg-red-500/20 text-red-400 border-red-500/50'
        }`}
      >
        {performance}
      </div>

      {/* Hover hint */}
      <div className="mt-2 text-xs text-center text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
        Click for details →
      </div>
    </motion.div>
  );
}

export default function CustomerResultsTable({ results, onCustomerClick }) {
  return (
    <div className="mt-8">
      <h3 className="text-xl font-semibold text-gray-50 mb-4">
        Your Customers ({results.length})
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {results.map((result, index) => (
          <motion.div
            key={`${result.customer.id}-${index}-${result.timing_data?.confirm_time || index}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 + index * 0.1 }}
          >
            <CustomerResultCard result={result} onClick={() => onCustomerClick(result)} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}
